import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { PayloadSelector } from "./PayloadSelector";
import { PayloadConfigPanel } from "./PayloadConfigPanel";
import * as payloadsApi from "@/services/payloadsApi";
import * as policiesApi from "@/services/policiesApi";
import type { PayloadSummary, CreatePayloadInput } from "@/types/api";

const STEPS = ["Policy Info", "Select Payloads", "Configure Payloads", "Review & Create"];

export function ConfigurationWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 1 state
  const [policyName, setPolicyName] = useState("");
  const [policyDescription, setPolicyDescription] = useState("");

  // Step 2 state
  const [selectedPayloads, setSelectedPayloads] = useState<PayloadSummary[]>([]);

  // Step 3 state: configs keyed by "kind:identifier"
  const [payloadConfigs, setPayloadConfigs] = useState<Record<string, Record<string, unknown>>>({});

  const canProceed = () => {
    switch (step) {
      case 0: return policyName.trim().length > 0;
      case 1: return selectedPayloads.length > 0;
      case 2: return true;
      case 3: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleConfigChange = (kind: string, identifier: string, config: Record<string, unknown>) => {
    setPayloadConfigs((prev) => ({ ...prev, [`${kind}:${identifier}`]: config }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Validate each payload
      for (const payload of selectedPayloads) {
        const key = `${payload.kind}:${payload.identifier}`;
        const config = payloadConfigs[key] || {};
        const validation = await payloadsApi.validatePayload(payload.kind!, payload.identifier!, config);
        if (!validation.valid) {
          toast({
            title: `Validation failed: ${payload.title || payload.identifier}`,
            description: validation.errors?.map((e: any) => e.message || JSON.stringify(e)).join(", ") || "Invalid configuration",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Create policy
      const payloads: CreatePayloadInput[] = selectedPayloads.map((p) => ({
        kind: p.kind!,
        identifier: p.identifier!,
        config: payloadConfigs[`${p.kind}:${p.identifier}`] || {},
      }));

      await policiesApi.createPolicy({
        name: policyName,
        description: policyDescription || undefined,
        payloads,
      });

      toast({ title: "Configuration created", description: `"${policyName}" has been created successfully.` });
      navigate("/configurations");
    } catch (error: any) {
      toast({
        title: "Failed to create configuration",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6 w-full">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/configurations")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">New Configuration</h1>
            <p className="text-sm text-muted-foreground">Create a policy with one or more MDM payloads</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-colors ${
                  i < step
                    ? "bg-primary text-primary-foreground"
                    : i === step
                    ? "bg-primary text-primary-foreground ring-2 ring-primary/30"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm hidden sm:inline ${i === step ? "font-medium" : "text-muted-foreground"}`}>
                {s}
              </span>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="border rounded-xl p-6 bg-card min-h-[400px]">
          {step === 0 && (
            <div className="space-y-4 max-w-lg">
              <div className="space-y-1.5">
                <Label htmlFor="policyName">Policy Name <span className="text-destructive">*</span></Label>
                <Input
                  id="policyName"
                  placeholder="e.g. Corporate Wi-Fi + VPN"
                  value={policyName}
                  onChange={(e) => setPolicyName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="policyDesc">Description</Label>
                <Textarea
                  id="policyDesc"
                  placeholder="Optional description of what this configuration does"
                  value={policyDescription}
                  onChange={(e) => setPolicyDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <PayloadSelector selected={selectedPayloads} onSelectionChange={setSelectedPayloads} />
          )}

          {step === 2 && (
            <div className="space-y-4">
              {selectedPayloads.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No payloads selected. Go back to select payloads.</p>
              ) : selectedPayloads.length === 1 ? (
                <PayloadConfigPanel
                  payload={selectedPayloads[0]}
                  config={payloadConfigs[`${selectedPayloads[0].kind}:${selectedPayloads[0].identifier}`] || {}}
                  onConfigChange={(config) =>
                    handleConfigChange(selectedPayloads[0].kind!, selectedPayloads[0].identifier!, config)
                  }
                />
              ) : (
                <Tabs defaultValue={`${selectedPayloads[0].kind}:${selectedPayloads[0].identifier}`}>
                  <TabsList className="flex-wrap h-auto gap-1">
                    {selectedPayloads.map((p) => (
                      <TabsTrigger key={`${p.kind}:${p.identifier}`} value={`${p.kind}:${p.identifier}`} className="text-xs">
                        {p.title || p.identifier}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {selectedPayloads.map((p) => (
                    <TabsContent key={`${p.kind}:${p.identifier}`} value={`${p.kind}:${p.identifier}`}>
                      <PayloadConfigPanel
                        payload={p}
                        config={payloadConfigs[`${p.kind}:${p.identifier}`] || {}}
                        onConfigChange={(config) => handleConfigChange(p.kind!, p.identifier!, config)}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Policy Name</h3>
                <p className="text-lg font-semibold">{policyName}</p>
                {policyDescription && <p className="text-sm text-muted-foreground mt-1">{policyDescription}</p>}
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Payloads ({selectedPayloads.length})
                </h3>
                <div className="space-y-3">
                  {selectedPayloads.map((p) => {
                    const key = `${p.kind}:${p.identifier}`;
                    const config = payloadConfigs[key] || {};
                    const configKeys = Object.keys(config).filter((k) => config[k] !== undefined && config[k] !== "");
                    return (
                      <div key={key} className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm">{p.title || p.identifier}</p>
                          <Badge variant="outline" className="text-xs">{p.kind}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{p.identifier}</p>
                        {configKeys.length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            {configKeys.length} field{configKeys.length !== 1 ? "s" : ""} configured
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack} disabled={step === 0}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={!canProceed()}>
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting || !canProceed()}>
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating…</>
              ) : (
                <><Check className="h-4 w-4 mr-1" /> Create Configuration</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
